import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import IntlMessages from '@src/util/IntlMessages';
import { Col, Form, Row, Select, DatePicker, Pagination } from 'antd';
import moment from 'moment';
import { useActiveEnterprises } from './hooks/useActiveEnterprises';
import { ResponsiveTable } from '@src/components/Table';
import { PERIOD_OPTIONS } from '../../components/constants';
import { useIntl } from 'react-intl';
import SearchBox from '@src/components/SearchBox';
import { DateUtil } from '@src/packages/utils/dates';

const DATE_FORMAT = DateUtil.DEFAULT_DT_DATE_FORMAT;

const ActiveEnterprises = ({ isActive = true }) => {
  const intl = useIntl();

  const {
    columns,
    data,
    loading,
    handleDownloadPdf,
    handlePrintPdf,
    handleDownloadDocx,
    handleDownloadExcel,
    pagination,
    totalFiltered,
    handleSearchChange,
    handlePeriodChange,
    handleStartDateChange,
    handleEndDateChange,
    form,
    filters,
  } = useActiveEnterprises({ isActive });

  const status = isActive ? 'active' : 'inactive';

  return (
    <BoxContainer>
      <BoxContainer fullContent shadow fixed>
        <FilterContainer
          title={
            <Title.Header
              value={<IntlMessages id={`work.direction.${status}.enterprises.report`} />}
            />
          }
          buttonItems={[
            {
              iconName: 'picture_as_pdf',
              action: () => handleDownloadPdf(columns, data),
              tooltipText: <IntlMessages id="general.download.pdf" />,
              disabled: loading || data.length === 0,
            },
            {
              iconName: 'article',
              action: () => handleDownloadDocx(columns, data),
              tooltipText: <IntlMessages id="general.download.docx" />,
              disabled: loading || data.length === 0,
            },
            {
              iconName: 'csv',
              action: () => handleDownloadExcel(columns, data),
              tooltipText: <IntlMessages id="general.download.excel" />,
              disabled: loading || data.length === 0,
            },
            {
              iconName: 'print',
              action: () => handlePrintPdf(columns, data),
              tooltipText: <IntlMessages id="general.print" />,
              disabled: loading || data.length === 0,
            },
          ]}
          content={
            <Form form={form}>
              <Row>
                <Col xs={24} sm={12} md={6} lg={6}>
                  <SearchBox
                    label={intl.formatMessage({ id: 'general.search' })}
                    placeholder={intl.formatMessage({ id: 'general.search' })}
                    onChange={handleSearchChange}
                    value={filters.search}
                    className="gx-w-100"
                  />
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                  <Title.LabelForm
                    value={<IntlMessages id="work.direction.filter.default.period" />}
                  />
                  <Select
                    placeholder={<IntlMessages id="work.direction.filter.period.placeholder" />}
                    name="period"
                    value={form.getFieldValue('period')}
                    onChange={handlePeriodChange}
                    options={PERIOD_OPTIONS.map(option => ({
                      ...option,
                      value: option.value === null ? undefined : option.value,
                    }))}
                    allowClear
                    className="gx-w-100"
                  />
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                  <Title.LabelForm value={<IntlMessages id="work.direction.filter.start.date" />} />
                  <DatePicker
                    value={form.getFieldValue('startDate')}
                    name="startDate"
                    format={DATE_FORMAT}
                    onChange={handleStartDateChange}
                    getPopupContainer={trigger => trigger.parentNode}
                    allowClear={true}
                    inputReadOnly
                    className="gx-w-100"
                  />
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                  <Title.LabelForm value={<IntlMessages id="work.direction.filter.end.date" />} />

                  <DatePicker
                    value={form.getFieldValue('endDate')}
                    name="endDate"
                    format={DATE_FORMAT}
                    onChange={handleEndDateChange}
                    getPopupContainer={trigger => trigger.parentNode}
                    allowClear={true}
                    className="gx-w-100"
                    disabledDate={current =>
                      current &&
                      current <
                        moment(form.getFieldValue('startDate')?.format(DATE_FORMAT), DATE_FORMAT)
                    }
                    inputReadOnly
                  />
                </Col>
              </Row>
            </Form>
          }
        />
      </BoxContainer>
      <BoxContainer fullContent>
        <ResponsiveTable columns={columns} dataSource={data} loading={loading} />
        {!loading && totalFiltered > 0 && (
          <div className="gx-d-flex gx-justify-content-center gx-align-items-center gx-mt-3">
            <Pagination {...pagination} />
          </div>
        )}
      </BoxContainer>
    </BoxContainer>
  );
};

export default ActiveEnterprises;
